import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getStaffingStatusStyle } from "@/components/staffing/WeeklyGapView/CountBadge";
import { INK } from "@/theme/tokens";
import { SheetActionRow } from "@/ui/Clickable/SheetActionRow";
import { EmptyState } from "@/ui/molecules/EmptyState";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import { formatStaffCount, isShownAsShort, shortfallOf } from "../gapsDayModel";
import { type GapsScopedShiftGroup, isSectionWriteBlocked } from "../gapsGroupSheets";
import { GAPS_DAY_LABEL_FORMAT } from "../gapsLabels";
import { useGapsCountsHidden } from "../useGapsCountsHidden";
import { GapsDateField } from "./GapsDateField";
import { GapsJobBadge } from "./GapsJobBadge";

type Props = {
	/** The day's groups as the home lists them (the saved scope applied). */
	groups: readonly GapsScopedShiftGroup[];
	date: DateTime;
	/** The home's week shortfalls, for the Date row's picker. */
	homeWeekShortfalls: readonly number[] | undefined;
	/** The Date row's pick: the shifts below are the picked day's (APP-886). */
	onPickDate: (date: DateTime) => void;
	onPick: (group: GapsScopedShiftGroup) => void;
	onClose: () => void;
};

const SHEET = "gaps-add-pick";

/**
 * "Add someone to a shift" from More's quick actions: which shift? The day's windows, the shortest-handed first
 * (largest shortfall on its printed value, ties in the home's own order), each with its `filled/required` in the
 * desktop's inks and its shortfall — so the shift that most needs a person is the first thing under the thumb.
 *
 * A window whose job section cannot say which job it is (`isSectionWriteBlocked`) is listed but not offered, with the
 * reason under it — the same refusal the home's group shows.
 */
export const GapsAddGroupPickStep: FC<Props> = ({ groups, date, homeWeekShortfalls, onPickDate, onPick, onClose }) => {
	const { t } = useTranslation();
	const { areCountsHidden } = useGapsCountsHidden();
	const ordered = useMemo(
		() =>
			groups
				.map((group, index) => ({ group, index, short: Number(formatStaffCount(shortfallOf(group))) }))
				.sort((a, b) => b.short - a.short || a.index - b.index)
				.map(({ group }) => group),
		[groups],
	);

	return (
		<SheetStep
			stepKey={SHEET}
			dataTestId={SHEET}
			title={t("gaps.addSheet.pickTitle")}
			subtitle={t("gaps.addSheet.pickSubtitle", { day: date.toFormat(GAPS_DAY_LABEL_FORMAT) })}
			footer={
				<SheetStepButton tone="dismiss" onClick={onClose} dataTestId={`${SHEET}-cancel`}>
					{t("cancel")}
				</SheetStepButton>
			}
		>
			<Box sx={{ px: 2, pt: 2, pb: 1 }}>
				<GapsDateField date={date} homeWeekShortfalls={homeWeekShortfalls} onPick={onPickDate} testIdPrefix={SHEET} />
			</Box>
			<Box data-testid={`${SHEET}-list`}>
				{ordered.map((group) => {
					const style = getStaffingStatusStyle(group.actual, group.required);
					const short = shortfallOf(group);
					const isBlocked = isSectionWriteBlocked(group);
					return (
						<SheetActionRow
							key={group.key}
							leading={<GapsJobBadge group={group} />}
							label={group.timeLabel}
							description={isBlocked ? t("gaps.group.ambiguousJob", { job: group.jobLabel }) : undefined}
							trailing={
								<Box component="span" sx={{ display: "inline-flex", gap: 0.75, alignItems: "baseline" }}>
									{!areCountsHidden && (
										<Box component="span" sx={{ color: style.isFlagged ? style.color : INK.SECONDARY, fontWeight: style.isFlagged ? 700 : 500 }}>
											{`${formatStaffCount(group.actual)}/${formatStaffCount(group.required)}`}
										</Box>
									)}
									{isShownAsShort(short) && (
										<Box component="span" sx={{ color: "error.main", fontWeight: 700 }}>
											{`−${formatStaffCount(short)}`}
										</Box>
									)}
								</Box>
							}
							showChevron={!isBlocked}
							disabled={isBlocked}
							onClick={() => onPick(group)}
							dataTestId={`${SHEET}-option-${group.key}`}
						/>
					);
				})}
				{ordered.length === 0 && <EmptyState dataTestId={`${SHEET}-empty`} title={t("gaps.empty.noShiftsOnDay")} sx={{ py: 4 }} />}
			</Box>
		</SheetStep>
	);
};
